import { FC, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Atom, MenuIcon } from "lucide-react";
import { useMobileMenu } from "@/contexts/MobileMenuProvider";
import { useAuthForm } from "@/contexts/AuthFormProvider";
import { Button } from "@/components/ui/button";

interface HeaderProps {}

const Header: FC<HeaderProps> = () => {
  const { setShowMenu, showMenu } = useMobileMenu();
  const [display, setDisplay] = useState(false);
  const { showLoginForm, showRegisterForm, closeForm } = useAuthForm();

  const scrollToSection = (sectionId: string) => {
    // Close any open form first
    closeForm();
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) { // Changed from 768 to 1024 for better tablet support
        setDisplay(true);
      } else {
        setDisplay(false);
      }
    }

    // Run on mount
    handleResize();

    // Run on resize event
    window.addEventListener('resize', handleResize);

    // Clean up event listener on unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <motion.div 
        className="fixed top-0 left-0 right-0 flex justify-center items-center py-6"
        style={{ zIndex: showMenu ? 10001 : 9999 }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {/* Minimalist background */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/90 to-transparent backdrop-blur-sm" style={{ zIndex: -1 }}></div>
        
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center">
      
          {/* Logo Section */}
          <motion.div 
            className="flex items-center relative z-10 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={() => {
              closeForm();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img
              src="/images/logo-light.png"
              alt="logo"
              width={120}
              height={50}
              className="brightness-110 h-8 sm:h-10 lg:h-12 w-auto"
            />
          </motion.div>

          {/* Navigation Section */}
          <div className="flex justify-between items-center gap-8 relative z-10">
            {display ? (
              <>
                {/* Features Link */}
                <motion.button 
                  className="text-gray-200 hover:text-blue-400 transition-all duration-300"
                  style={{ fontFamily: 'GilroyMedium' }}
                  onClick={() => scrollToSection('features')}
                  whileHover={{ scale: 1.05 }}
                >
                  Features
                </motion.button>

                {/* Divider */}
                <div className="w-px h-6 bg-gradient-to-b from-transparent via-gray-500 to-transparent"></div>

                {/* Pricing Link */}
                <motion.button 
                  className="text-gray-200 hover:text-blue-400 transition-all duration-300"
                  style={{ fontFamily: 'GilroyMedium' }}
                  onClick={() => scrollToSection('pricing')}
                  whileHover={{ scale: 1.05 }}
                >
                  Pricing
                </motion.button>

                {/* Divider */}
                <div className="w-px h-6 bg-gradient-to-b from-transparent via-gray-500 to-transparent"></div>

                {/* Login Button */}
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    loading={false}
                    className="text-gray-200 bg-transparent hover:text-blue-400 transition-all duration-300"
                    style={{ fontFamily: 'GilroyMedium' }}
                    onClick={showLoginForm}
                  >
                    Login
                  </Button>
                </motion.div>

                {/* Sign Up Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    loading={false}
                    icon={<Atom className="w-5 h-5" />}
                    onClick={() => {
                      window.lintrk('track', { conversion_id: 23344122 });
                      showRegisterForm();
                    }}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold hover:from-blue-700 hover:to-blue-800 px-6 py-3 rounded-xl transition-all duration-300"
                    style={{
                      fontFamily: 'GilroyMedium',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    Sign Up
                  </Button>
                </motion.div>
              </>
            ) : (
              <motion.div 
                whileHover={{ scale: 1.1 }} 
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  loading={false} 
                  onClick={(_e) => {
                    console.log('🔍 Header hamburger clicked, current showMenu:', showMenu);
                    setShowMenu(!showMenu);
                  }}
                  className="p-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl backdrop-blur-sm transition-all duration-300"
                >
                  <MenuIcon className="w-6 h-6" />
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
      
      {/* Full-width bottom line separator */}
      <motion.div
        className="fixed top-20 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-600/50 to-transparent"
        style={{ zIndex: 9998 }}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
      />
    </>
  );
};

export default Header;